/**
 * Authentication Context
 *
 * Provides global authentication state and methods for the entire application.
 * Manages JWT tokens, user profile, login/logout, and automatic token refresh.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import * as api from '@/lib/api';
import * as authLib from '@/lib/auth';
import type {
  AuthContextValue,
  AuthState,
  AuthTokens,
  LoginRequest,
  LoginResponse,
  NewPasswordRequest,
  TokenRefreshResult,
} from '@/types/auth';

/**
 * Initial Authentication State
 */
const initialState: AuthState = {
  tokens: null,
  user: null,
  isLoading: true, // Start as loading to check localStorage
  error: null,
};

/**
 * Authentication Context
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Auth Provider Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Wraps the application and provides authentication state and methods
 * to all child components via context.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);

  /**
   * Initialize Authentication State
   * Restores auth state from localStorage on app load
   */
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const tokens = authLib.loadTokens();
        const user = authLib.loadUserProfile();

        if (tokens && user && !authLib.isTokenExpired(tokens.accessToken)) {
          // Valid tokens found - restore auth state
          setState({
            tokens,
            user,
            isLoading: false,
            error: null,
          });

          // Set auth token for API client
          api.setAuthToken(tokens.idToken);
        } else {
          // No valid tokens - clear stale data
          authLib.clearAuthData();
          setState({
            tokens: null,
            user: null,
            isLoading: false,
            error: null,
          });
        }
      } catch (error: unknown) {
        console.error('Failed to initialize auth state:', error);
        authLib.clearAuthData();
        setState({
          tokens: null,
          user: null,
          isLoading: false,
          error: 'Failed to restore authentication state',
        });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Set Authentication State
   * Helper to update state with tokens and user profile
   */
  const setAuthState = useCallback((tokens: AuthTokens) => {
    // Extract user profile from ID token
    const user = authLib.extractUserProfile(tokens.idToken);

    if (!user) {
      throw new Error('Failed to extract user profile from token');
    }

    // Save to localStorage
    authLib.saveTokens(tokens);
    authLib.saveUserProfile(user);

    // Update state
    setState({
      tokens,
      user,
      isLoading: false,
      error: null,
    });

    // Set auth token for API client
    api.setAuthToken(tokens.idToken);
  }, []);

  /**
   * Login
   * Authenticate user with username and password
   */
  const login = useCallback(
    async (credentials: LoginRequest): Promise<LoginResponse> => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await api.login(credentials);

        // Check if password change is required
        if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setState(prev => ({ ...prev, isLoading: false }));
          return response; // Return challenge response
        }

        // Successful login - store tokens
        if (response.authenticationResult) {
          const tokens: AuthTokens = {
            accessToken: response.authenticationResult.accessToken,
            idToken: response.authenticationResult.idToken,
            refreshToken: response.authenticationResult.refreshToken,
            expiresAt: authLib.calculateExpiryTimestamp(response.authenticationResult.expiresIn),
          };

          setAuthState(tokens);
        }

        return response;
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Login failed';

        setState({
          tokens: null,
          user: null,
          isLoading: false,
          error: errorMessage,
        });

        throw error;
      }
    },
    [setAuthState]
  );

  /**
   * Respond to New Password Challenge
   * Complete NEW_PASSWORD_REQUIRED challenge with new password
   */
  const respondToNewPassword = useCallback(
    async (request: NewPasswordRequest): Promise<void> => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await api.respondToNewPassword(request);

        // Store tokens after successful password change
        const tokens: AuthTokens = {
          accessToken: response.authenticationResult.accessToken,
          idToken: response.authenticationResult.idToken,
          refreshToken: response.authenticationResult.refreshToken,
          expiresAt: authLib.calculateExpiryTimestamp(response.authenticationResult.expiresIn),
        };

        setAuthState(tokens);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Password change failed';

        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));

        throw error;
      }
    },
    [setAuthState]
  );

  /**
   * Logout
   * Clear authentication state and remove tokens
   */
  const logout = useCallback(() => {
    // Clear localStorage
    authLib.clearAuthData();

    // Clear API client auth token
    api.setAuthToken(null);

    // Reset state
    setState({
      tokens: null,
      user: null,
      isLoading: false,
      error: null,
    });
  }, []);

  /**
   * Refresh Tokens
   * Manually refresh authentication tokens
   * Note: Automatic refresh is not implemented yet - would require backend endpoint
   */
  const refreshTokens = useCallback(async (): Promise<TokenRefreshResult> => {
    // TODO: Implement token refresh when backend endpoint is available
    // For now, just return failure and require re-login
    return {
      success: false,
      error: 'Token refresh not yet implemented. Please log in again.',
    };
  }, []);

  /**
   * Get Current Access Token
   * Returns current access token if valid
   */
  const getAccessToken = useCallback((): string | null => {
    if (!state.tokens) {
      return null;
    }

    // Check if token is expired
    if (authLib.isTokenExpired(state.tokens.accessToken)) {
      // Token expired - logout user
      logout();
      return null;
    }

    return state.tokens.accessToken;
  }, [state.tokens, logout]);

  /**
   * Get Current ID Token
   * Returns current ID token if valid (required for audit log API authentication)
   */
  const getIdToken = useCallback((): string | null => {
    if (!state.tokens) {
      return null;
    }

    // Check if access token is expired (ID token has same expiry)
    if (authLib.isTokenExpired(state.tokens.accessToken)) {
      // Token expired - logout user
      logout();
      return null;
    }

    return state.tokens.idToken;
  }, [state.tokens, logout]);

  /**
   * Check if User is Authenticated
   */
  const isAuthenticated =
    state.tokens !== null &&
    state.user !== null &&
    !authLib.isTokenExpired(state.tokens.accessToken);

  /**
   * Automatic Token Refresh Check
   * Periodically checks if tokens need refresh (every minute)
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      if (authLib.shouldRefreshToken()) {
        // Token will expire soon - logout user
        // TODO: Replace with automatic refresh when backend supports it
        console.warn('Token expiring soon. Please log in again.');
        logout();
      }
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [isAuthenticated, logout]);

  const contextValue: AuthContextValue = {
    state,
    login,
    respondToNewPassword,
    logout,
    isAuthenticated,
    getAccessToken,
    getIdToken,
    refreshTokens,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

/**
 * Use Auth Hook
 * Custom hook to access authentication context
 *
 * @returns Authentication context value
 * @throws {Error} If used outside AuthProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
